// Astral Dominion content (Bible AD-01..AD-25). Bible-specified where stated; TEST
// VALUES otherwise. Core HP scaled by the owner's ~0.53 TTK factor (6,300 → 3,350).

import type {
  AbilityDef,
  BossExtras,
  BuildingDef,
  CardDef,
  RitualDef,
  SpellDef,
  TrapDef,
  UnitDef,
} from './types';

// ---------------------------------------------------------------- units

export const AD_UNITS: UnitDef[] = [
  {
    id: 'u-ad-worker',
    name: 'Lattice Artisan',
    faction: 'astral-dominion',
    sizeTag: 'small',
    roleTags: ['worker'],
    squadMemberCount: 1,
    maxHealth: 52,
    armor: 'none',
    moveSpeed: 3.3,
    targetPriority: [],
    abilities: [],
    command: 0,
    captureWeight: 0,
    ui: { color: 0x9fb8d8, scale: 0.55, label: 'Artisan' },
  },
  {
    id: 'u-ad-blink-infantry',
    name: 'Blink Infantry',
    faction: 'astral-dominion',
    sizeTag: 'small',
    roleTags: ['frontline'],
    squadMemberCount: 5, // bible AD-09
    maxHealth: 600, // TEST
    armor: 'moderate',
    moveSpeed: 3.4,
    attack: { damage: 40, period: 1.0, range: 1.6 },
    targetPriority: ['squad', 'worker', 'building'],
    abilities: ['ab-ad-tactical-blink'],
    command: 3,
    captureWeight: 1,
    ui: { color: 0x8fb0d8, scale: 0.85, label: 'Blink Infantry' },
  },
  {
    id: 'u-ad-arc-snipers',
    name: 'Arc Snipers',
    faction: 'astral-dominion',
    sizeTag: 'small',
    roleTags: ['ranged'],
    squadMemberCount: 3, // bible AD-10
    maxHealth: 420, // TEST
    armor: 'light',
    moveSpeed: 3.0,
    attack: {
      damage: 62,
      period: 1.8,
      range: 14,
      projectileSpeed: 36,
      damageVs: { large: 1.2, giant: 1.2 },
    },
    targetPriority: ['worker', 'squad', 'siege', 'large'],
    abilities: [],
    command: 4,
    captureWeight: 0.5,
    ui: { color: 0x6fd8e8, scale: 0.85, label: 'Arc Snipers' },
  },
  {
    id: 'u-ad-phase-tank',
    name: 'Phase Assault Tank',
    faction: 'astral-dominion',
    sizeTag: 'large',
    roleTags: ['siege', 'frontline'],
    squadMemberCount: 1,
    maxHealth: 1750, // TEST
    armor: 'heavy',
    moveSpeed: 2.4,
    attack: { damage: 98, period: 1.7, range: 9, projectileSpeed: 30, structureMult: 1.4 },
    targetPriority: ['building', 'trap', 'siege', 'squad'],
    abilities: ['ab-ad-phase-through'],
    command: 8,
    captureWeight: 0.5,
    ui: { color: 0x7a90b8, scale: 1.35, label: 'Phase Tank' },
  },
  {
    id: 'u-ad-targeting-construct',
    name: 'Targeting Construct',
    faction: 'astral-dominion',
    sizeTag: 'small',
    roleTags: ['support'],
    squadMemberCount: 1, // bible AD-12
    maxHealth: 360, // TEST
    armor: 'light',
    moveSpeed: 3.2,
    attack: { damage: 8, period: 1.6, range: 10, projectileSpeed: 30 },
    targetPriority: ['squad'],
    abilities: ['ab-ad-priority-lattice'],
    command: 3,
    captureWeight: 0.5,
    ui: { color: 0xb8d0e8, scale: 0.8, label: 'Targeting' },
  },
  {
    id: 'u-ad-shield-drone',
    name: 'Shield Drone',
    faction: 'astral-dominion',
    sizeTag: 'small',
    roleTags: ['support'],
    squadMemberCount: 3, // bible AD-13
    maxHealth: 440, // TEST
    armor: 'light',
    moveSpeed: 3.3,
    attack: { damage: 10, period: 1.5, range: 8, projectileSpeed: 26 },
    targetPriority: ['squad'],
    abilities: ['ab-ad-intercept-matrix'],
    command: 3,
    captureWeight: 0.5,
    ui: { color: 0x8fd0e0, scale: 0.8, label: 'Shield Drone' },
  },
  {
    id: 'u-ad-phase-engineer',
    name: 'Phase Engineer',
    faction: 'astral-dominion',
    sizeTag: 'small',
    roleTags: ['worker', 'builder'],
    squadMemberCount: 1,
    maxHealth: 78, // TEST — armored engineer construct
    armor: 'light',
    moveSpeed: 3.3,
    targetPriority: [],
    abilities: [],
    command: 0,
    captureWeight: 0,
    workerVariant: { buildMult: 1.4, repairMult: 1.25, carryMult: 1.1 },
    ui: { color: 0xc8d8e8, scale: 0.66, label: 'Phase Engineer' },
  },
  {
    id: 'u-ad-star-lancer',
    name: 'Star Lancer',
    faction: 'astral-dominion',
    sizeTag: 'large',
    roleTags: ['raider'],
    squadMemberCount: 1,
    maxHealth: 1350, // TEST
    armor: 'moderate',
    moveSpeed: 4.2,
    attack: { damage: 88, period: 1.4, range: 2.5, damageVs: { large: 1.4, giant: 1.7 } }, // TEST — giant-killer bump
    targetPriority: ['giant', 'large', 'siege', 'squad'],
    abilities: ['ab-ad-comet-pass', 'ab-cavalry-charge'],
    command: 7,
    captureWeight: 0.5,
    ui: { color: 0xa8c8f0, scale: 1.3, label: 'Star Lancer' },
  },
  {
    id: 'u-ad-siege-orb',
    name: 'Siege Orb',
    faction: 'astral-dominion',
    sizeTag: 'large',
    roleTags: ['siege', 'ranged'],
    squadMemberCount: 1,
    maxHealth: 1400, // TEST
    armor: 'moderate',
    moveSpeed: 2.2,
    attack: {
      damage: 125,
      period: 2.5,
      range: 15,
      projectileSpeed: 16,
      structureMult: 1.6,
      damageVs: { giant: 1.2 },
    },
    targetPriority: ['building', 'siege', 'trap', 'squad'],
    abilities: [],
    command: 8,
    captureWeight: 0.5,
    ui: { color: 0x6a80c0, scale: 1.3, label: 'Siege Orb' },
  },
  {
    id: 'u-ad-rift-hounds',
    name: 'Rift Hounds',
    faction: 'astral-dominion',
    sizeTag: 'small',
    roleTags: ['raider'],
    squadMemberCount: 5, // bible v0.2.4 AD-04
    maxHealth: 380, // TEST
    armor: 'none',
    moveSpeed: 4.4, // TEST — fast pursuit pack
    attack: { damage: 30, period: 0.9, range: 1.4 },
    targetPriority: ['squad', 'worker'],
    abilities: ['ab-ad-phase-chase'],
    command: 2,
    captureWeight: 1,
    ui: { color: 0x9a86d8, scale: 0.6, label: 'Rift Hounds' },
  },
  {
    id: 'u-ad-prism-adepts',
    name: 'Prism Adepts',
    faction: 'astral-dominion',
    sizeTag: 'small',
    roleTags: ['ranged', 'support'],
    squadMemberCount: 3, // bible v0.2.4 AD-08
    maxHealth: 400, // TEST
    armor: 'light',
    moveSpeed: 3.0,
    // Refract Beam folded into a long-range beam attack (deviation)
    attack: { damage: 56, period: 1.9, range: 13, projectileSpeed: 42 }, // TEST
    targetPriority: ['squad', 'siege', 'large'],
    abilities: ['ab-ad-prism-ward'],
    command: 4,
    captureWeight: 0.5,
    maxActiveUnits: 2, // bible AD-08: maximum two active
    ui: { color: 0xc9a8f0, scale: 0.85, label: 'Prism Adepts' },
  },
  {
    id: 'u-ad-null-monks',
    name: 'Null Monks',
    faction: 'astral-dominion',
    sizeTag: 'small',
    roleTags: ['support', 'frontline'],
    squadMemberCount: 4, // bible v0.2.4 AD-09
    maxHealth: 500, // TEST
    armor: 'light',
    moveSpeed: 3.1,
    attack: { damage: 34, period: 1.1, range: 1.6 }, // TEST
    targetPriority: ['squad', 'large'],
    abilities: ['ab-ad-null-field', 'ab-ad-borrowed-formula'],
    command: 4,
    captureWeight: 0.5,
    maxActiveUnits: 2, // bible AD-09: maximum two active
    ui: { color: 0x8a76b8, scale: 0.85, label: 'Null Monks' },
  },
  {
    id: 'u-ad-comet-riders',
    name: 'Comet Riders',
    faction: 'astral-dominion',
    sizeTag: 'large',
    roleTags: ['raider', 'siege'],
    squadMemberCount: 3, // bible v0.2.4 AD-10
    maxHealth: 950, // TEST
    armor: 'light',
    moveSpeed: 4.6, // TEST — flying in the bible; implemented as a fast hover unit (deviation)
    attack: { damage: 58, period: 1.4, range: 2.5, structureMult: 1.3 }, // TEST
    targetPriority: ['building', 'siege', 'squad'],
    abilities: ['ab-ad-comet-dive', 'ab-cavalry-charge'],
    command: 5,
    captureWeight: 0.5,
    maxActiveUnits: 3, // bible AD-10: maximum three active
    ui: { color: 0xf0cf8a, scale: 1.2, label: 'Comet Riders' },
  },
  {
    id: 'u-ad-vector-blades',
    name: 'Vector Blades',
    faction: 'astral-dominion',
    sizeTag: 'small',
    roleTags: ['raider'],
    squadMemberCount: 4, // bible v0.2.4 AD-13
    maxHealth: 560, // TEST
    armor: 'moderate',
    moveSpeed: 3.8, // TEST
    attack: { damage: 52, period: 1.0, range: 1.6 }, // TEST
    targetPriority: ['worker', 'squad', 'siege'],
    abilities: ['ab-ad-weak-side-translation'],
    command: 5,
    captureWeight: 0.5,
    ui: { color: 0xb08ae0, scale: 0.9, label: 'Vector Blades' },
  },
  {
    id: 'u-ad-gravity-weavers',
    name: 'Gravity Weavers',
    faction: 'astral-dominion',
    sizeTag: 'small',
    roleTags: ['support'],
    squadMemberCount: 3, // bible v0.2.4 AD-14
    maxHealth: 420, // TEST
    armor: 'light',
    moveSpeed: 2.9,
    attack: { damage: 40, period: 2.0, range: 10, projectileSpeed: 24 }, // TEST
    targetPriority: ['squad', 'large'],
    abilities: ['ab-ad-suspension-knot', 'ab-ad-mass-inversion'],
    command: 4,
    captureWeight: 0.5,
    maxActiveUnits: 2, // bible AD-14: maximum two active
    ui: { color: 0x6f5fae, scale: 0.85, label: 'Gravity Weavers' },
  },
  {
    id: 'u-ad-celestial-wardens',
    name: 'Celestial Wardens',
    faction: 'astral-dominion',
    sizeTag: 'large',
    roleTags: ['frontline', 'support'],
    squadMemberCount: 2, // bible v0.2.4 AD-15 — construct pair
    maxHealth: 1500, // TEST
    armor: 'heavy',
    moveSpeed: 2.4,
    attack: { damage: 66, period: 1.6, range: 6, projectileSpeed: 26 }, // TEST
    targetPriority: ['squad', 'siege', 'large'],
    abilities: ['ab-ad-mirror-shield', 'ab-ad-anchor-field'],
    command: 6,
    captureWeight: 0.5,
    ui: { color: 0xe6ddb0, scale: 1.35, label: 'Wardens' },
  },
  {
    id: 'u-ad-chrono-lancers',
    name: 'Chrono Lancers',
    faction: 'astral-dominion',
    sizeTag: 'medium',
    roleTags: ['raider', 'anti-large'],
    squadMemberCount: 5, // bible v0.2.4 AD-16
    maxHealth: 900, // TEST
    armor: 'moderate',
    moveSpeed: 4.4, // TEST — elite cavalry
    attack: { damage: 70, period: 1.2, range: 2.0, damageVs: { large: 1.25 } }, // TEST
    targetPriority: ['large', 'siege', 'squad'],
    abilities: ['ab-ad-time-split', 'ab-ad-rewind-position', 'ab-cavalry-charge'],
    command: 8,
    captureWeight: 0.5,
    ui: { color: 0x74d8c0, scale: 0.95, label: 'Chrono Lancers' },
  },
  {
    id: 'u-ad-solar-ark',
    name: 'Solar Ark',
    faction: 'astral-dominion',
    sizeTag: 'giant',
    roleTags: ['ranged', 'support', 'siege'],
    squadMemberCount: 1, // bible v0.2.4 AD-17
    maxHealth: 2250, // TEST — giant HP pass: −25% so focused fire pays off
    armor: 'heavy',
    moveSpeed: 2.0, // TEST — flying in the bible; implemented as a slow hover platform (deviation)
    // Sunline Beam ramp folded into a heavy long-range beam attack (deviation)
    attack: {
      damage: 112, // TEST
      period: 1.8,
      range: 13,
      projectileSpeed: 44,
      structureMult: 1.3,
      damageVs: { large: 1.2 },
    },
    targetPriority: ['building', 'large', 'squad'],
    abilities: ['ab-ad-drone-deck', 'ab-ad-drone-deck-reserve', 'ab-ad-ark-charge'],
    command: 10,
    captureWeight: 0.5,
    ui: { color: 0xf8e6a0, scale: 2.2, label: 'Solar Ark' },
  },
  {
    id: 'boss-ad-xyra',
    name: 'Phase Hunter Xyra',
    faction: 'astral-dominion',
    sizeTag: 'large',
    roleTags: ['boss', 'raider'],
    squadMemberCount: 1,
    maxHealth: 725, // halved from previous prototype value per owner feedback
    armor: 'moderate',
    moveSpeed: 4.0,
    attack: { damage: 105, period: 1.1, range: 2.2 },
    targetPriority: ['worker', 'squad', 'building'],
    abilities: ['ab-ad-folded-pursuit'],
    command: 7,
    isBoss: true,
    captureWeight: 0.5,
    ui: { color: 0x9fb8e8, scale: 1.6, label: 'Xyra' },
  },
  {
    id: 'boss-ad-arbiter',
    name: 'Celestial Arbiter',
    faction: 'astral-dominion',
    sizeTag: 'large',
    roleTags: ['boss', 'support'],
    squadMemberCount: 1,
    maxHealth: 1800, // halved from previous prototype value per owner feedback
    armor: 'heavy',
    moveSpeed: 2.6,
    attack: { damage: 70, period: 1.5, range: 9, projectileSpeed: 28 },
    targetPriority: ['squad', 'building'],
    abilities: ['ab-ad-adjudicate-position'],
    command: 12,
    isBoss: true,
    captureWeight: 0.5,
    ui: { color: 0xd0d8f0, scale: 1.9, label: 'Arbiter' },
  },
  {
    id: 'boss-ad-war-engine',
    name: 'Celestial War Engine',
    faction: 'astral-dominion',
    sizeTag: 'giant',
    roleTags: ['boss', 'siege', 'ranged'],
    squadMemberCount: 1,
    maxHealth: 2650, // halved from previous prototype value per owner feedback
    armor: 'heavy',
    moveSpeed: 1.9,
    attack: {
      damage: 140,
      period: 2.0,
      range: 14,
      projectileSpeed: 30,
      structureMult: 1.7,
      damageVs: { giant: 1.3 },
    },
    targetPriority: ['building', 'giant', 'squad'],
    abilities: ['ab-ad-solar-measure', 'ab-ad-defensive-translation'],
    command: 20,
    isBoss: true,
    captureWeight: 0.5,
    ui: { color: 0xe8ecf8, scale: 2.5, label: 'War Engine' },
  },
  {
    // Core Annex System (owner spec 2026-07-13): hovering relay-carrier created by
    // the Conduit Nexus. E: Establish Astral Relay phases it into a network node.
    id: 'u-ad-prism-skiff',
    name: 'Prism Skiff',
    faction: 'astral-dominion',
    sizeTag: 'small',
    roleTags: ['support'],
    squadMemberCount: 1,
    maxHealth: 520, // TEST — fragile logistics craft
    armor: 'none',
    moveSpeed: 3.4, // TEST — hovers
    targetPriority: [],
    abilities: [],
    command: 0, // deployable building-carrier, not an army unit
    captureWeight: 0,
    deploysInto: {
      buildingId: 'b-ad-astral-relay',
      assembleSeconds: 40, // spec: phases into a free Relay over 40 s
      maxDeployed: 2, // spec: maximum two active Relays from this annex
      witherSeconds: 8, // TEST — a third deployment destabilizes the oldest
      label: 'Establish Astral Relay',
    },
    ui: { color: 0xb8d0f0, scale: 1.0, label: 'Prism Skiff' },
  },
];

// ---------------------------------------------------------------- abilities

export const AD_ABILITIES: AbilityDef[] = [
  {
    // bible AD-09 Tactical Blink
    id: 'ab-ad-tactical-blink',
    name: 'Tactical Blink',
    trigger: { when: 'surroundedBy', count: 3, range: 5 },
    internalCooldown: 20,
    telegraphSeconds: 0.3,
    targetSelector: 'self',
    effects: [{ fx: 'teleport', distance: 8, direction: 'away-from-target' }],
    ui: { label: 'Tactical Blink', showFloatingLabel: true },
  },
  {
    // bible AD-11 Phase Through (blocked-by-smalls approximated as a crowd trigger)
    id: 'ab-ad-phase-through',
    name: 'Phase Through',
    trigger: { when: 'surroundedBy', count: 2, range: 4, sizeTags: ['small', 'medium'] },
    internalCooldown: 16,
    telegraphSeconds: 0.3,
    targetSelector: 'self',
    effects: [
      { fx: 'areaDamage', amount: 45, radius: 3 },
      { fx: 'teleport', distance: 6, direction: 'toward-target' },
    ],
    ui: { label: 'Phase Through', showFloatingLabel: true },
  },
  {
    // bible AD-12 Priority Lattice (damage-amp mark folded into an aura — deviation)
    id: 'ab-ad-priority-lattice',
    name: 'Priority Lattice',
    trigger: { when: 'timer', period: 16 },
    internalCooldown: 16,
    telegraphSeconds: 0.4,
    targetSelector: 'self',
    effects: [{ fx: 'auraBuff', radius: 11, stat: 'damage', mult: 1.12, duration: 7 }],
    ui: { label: 'Priority Lattice', showFloatingLabel: true },
  },
  {
    // bible AD-13 Intercept Matrix
    id: 'ab-ad-intercept-matrix',
    name: 'Intercept Matrix',
    trigger: { when: 'timer', period: 18 },
    internalCooldown: 18,
    telegraphSeconds: 0.4,
    targetSelector: 'self',
    effects: [{ fx: 'healLowestAlly', amount: 0, range: 10 }, { fx: 'auraBuff', radius: 10, stat: 'damageTaken', mult: 0.9, duration: 6 }],
    ui: { label: 'Intercept Matrix', showFloatingLabel: false },
  },
  {
    // bible AD-15 Comet Pass
    id: 'ab-ad-comet-pass',
    name: 'Comet Pass',
    trigger: { when: 'largeEnemyInRange', range: 9 },
    internalCooldown: 18,
    telegraphSeconds: 0.4,
    targetSelector: 'triggeringEntity',
    effects: [
      { fx: 'damage', amount: 160 },
      { fx: 'teleport', distance: 7, direction: 'toward-target' },
    ],
    ui: { label: 'Comet Pass', showFloatingLabel: true },
  },
  {
    // bible AD-23 Folded Pursuit
    id: 'ab-ad-folded-pursuit',
    name: 'Folded Pursuit',
    trigger: { when: 'targetIsolated', range: 9 },
    internalCooldown: 15,
    telegraphSeconds: 0.3,
    targetSelector: 'currentTarget',
    effects: [
      { fx: 'teleport', distance: 9, direction: 'toward-target' },
      { fx: 'statMod', stat: 'attackSpeed', mult: 1.2, duration: 4 },
    ],
    ui: { label: 'Folded Pursuit', showFloatingLabel: true },
  },
  {
    // bible AD-24 Adjudicate Position — the one HOSTILE displacement in the game
    id: 'ab-ad-adjudicate-position',
    name: 'Adjudicate Position',
    trigger: { when: 'timer', period: 17 },
    internalCooldown: 17,
    telegraphSeconds: 0.6,
    targetSelector: 'currentTarget',
    effects: [{ fx: 'knockback', distance: 10 }],
    ui: { label: 'Adjudicate Position', showFloatingLabel: true },
  },
  {
    // bible AD-25 Solar Measure
    id: 'ab-ad-solar-measure',
    name: 'Solar Measure',
    trigger: { when: 'nthAttackVsStructure', n: 3 },
    internalCooldown: 2,
    telegraphSeconds: 0.6,
    targetSelector: 'currentTarget',
    effects: [{ fx: 'structureStrike', damage: 550, repairHalfSeconds: 0 }],
    ui: { label: 'Solar Measure', showFloatingLabel: true },
  },
  {
    // bible AD-25 Defensive Translation
    id: 'ab-ad-defensive-translation',
    name: 'Defensive Translation',
    trigger: { when: 'surroundedBy', count: 3, range: 4 },
    internalCooldown: 24,
    telegraphSeconds: 0.4,
    targetSelector: 'self',
    effects: [
      { fx: 'areaDamage', amount: 40, radius: 4 },
      { fx: 'teleport', distance: 8, direction: 'away-from-target' },
    ],
    ui: { label: 'Defensive Translation', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 AD-04 Phase Chase + Dimensional Bite (blink-follow approximated as an
    // isolated-target pursuit teleport — deviation; the slow stands in for teleport-range loss)
    id: 'ab-ad-phase-chase',
    name: 'Phase Chase',
    trigger: { when: 'targetIsolated', range: 8 },
    internalCooldown: 16, // bible: once every 16 seconds
    telegraphSeconds: 0.3,
    targetSelector: 'currentTarget',
    effects: [
      { fx: 'teleport', distance: 8, direction: 'toward-target' },
      { fx: 'slow', magnitude: 0.35, duration: 5 }, // Dimensional Bite: 5 s (bible)
    ],
    ui: { label: 'Phase Chase', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 AD-08 Prism Ward (spell-only shield approximated as a periodic
    // damage-taken aura — deviation)
    id: 'ab-ad-prism-ward',
    name: 'Prism Ward',
    trigger: { when: 'timer', period: 18 }, // TEST
    internalCooldown: 18,
    telegraphSeconds: 0.4,
    targetSelector: 'self',
    effects: [{ fx: 'auraBuff', radius: 9, stat: 'damageTaken', mult: 0.85, duration: 5 }], // TEST
    ui: { label: 'Prism Ward', showFloatingLabel: false },
  },
  {
    // bible v0.2.4 AD-09 Null Field (zone-expiry/shield-regen denial approximated as a
    // recovery + damage debuff on the current target — deviation)
    id: 'ab-ad-null-field',
    name: 'Null Field',
    trigger: { when: 'timer', period: 15 }, // TEST
    internalCooldown: 15,
    telegraphSeconds: 0.4,
    targetSelector: 'currentTarget',
    effects: [
      { fx: 'healReduction', mult: 0.6, duration: 6 }, // TEST
      { fx: 'statMod', stat: 'damage', mult: 0.85, duration: 6 }, // TEST
    ],
    ui: { label: 'Null Field', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 AD-09 Borrowed Formula (stolen-buff copy approximated as a periodic
    // allied damage aura — deviation)
    id: 'ab-ad-borrowed-formula',
    name: 'Borrowed Formula',
    trigger: { when: 'timer', period: 22 }, // TEST
    internalCooldown: 22,
    telegraphSeconds: 0.4,
    targetSelector: 'self',
    effects: [{ fx: 'auraBuff', radius: 8, stat: 'damage', mult: 1.08, duration: 6 }], // TEST, 6 s (bible)
    ui: { label: 'Borrowed Formula', showFloatingLabel: false },
  },
  {
    // bible v0.2.4 AD-10 Comet Dive + Burning Tail (trail folded into the impact splash —
    // deviation; attack-speed debuff carries the building-suppression clause)
    id: 'ab-ad-comet-dive',
    name: 'Comet Dive',
    trigger: { when: 'attackingTargetTag', tag: 'building' },
    internalCooldown: 18, // TEST
    telegraphSeconds: 0.5,
    targetSelector: 'currentTarget',
    effects: [
      { fx: 'teleport', distance: 8, direction: 'toward-target' },
      { fx: 'areaDamage', amount: 90, radius: 3 }, // TEST
      { fx: 'statMod', stat: 'attackSpeed', mult: 0.75, duration: 4 }, // TEST
    ],
    ui: { label: 'Comet Dive', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 AD-13 Weak-Side Translation + Sever Link (support/ranged-order condition
    // approximated as an isolated-target trigger; aura disable approximated as a damage
    // debuff — deviation)
    id: 'ab-ad-weak-side-translation',
    name: 'Weak-Side Translation',
    trigger: { when: 'targetIsolated', range: 10 },
    internalCooldown: 18, // TEST
    telegraphSeconds: 1.5, // bible: 1.5-second telegraph
    targetSelector: 'currentTarget',
    effects: [
      { fx: 'teleport', distance: 10, direction: 'toward-target' },
      { fx: 'statMod', stat: 'damage', mult: 0.8, duration: 5 }, // Sever Link: 5 s (bible)
    ],
    ui: { label: 'Weak-Side Translation', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 AD-14 Suspension Knot (lift approximated as root + damage-taken
    // reduction on the triggering squad — deviation)
    id: 'ab-ad-suspension-knot',
    name: 'Suspension Knot',
    trigger: { when: 'surroundedBy', count: 2, range: 9, sizeTags: ['small', 'medium'] },
    internalCooldown: 20, // TEST
    telegraphSeconds: 0.6,
    targetSelector: 'triggeringEntity',
    effects: [
      { fx: 'root', duration: 3 }, // TEST
      { fx: 'statMod', stat: 'damageTaken', mult: 0.7, duration: 3 }, // TEST
    ],
    ui: { label: 'Suspension Knot', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 AD-14 Mass Inversion
    id: 'ab-ad-mass-inversion',
    name: 'Mass Inversion',
    trigger: { when: 'chargedBy', sizeTags: ['large', 'giant'] },
    internalCooldown: 16, // TEST
    telegraphSeconds: 0.3,
    targetSelector: 'triggeringEntity',
    effects: [
      { fx: 'knockback', distance: 6 }, // TEST
      { fx: 'slow', magnitude: 0.35, duration: 5 }, // TEST
    ],
    ui: { label: 'Mass Inversion', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 AD-15 Mirror Shield (projectile reflection approximated as direct
    // damage back at the shooter — deviation)
    id: 'ab-ad-mirror-shield',
    name: 'Mirror Shield',
    trigger: { when: 'targetedByRangedSquads', count: 1 },
    internalCooldown: 14, // bible: every 14 seconds
    telegraphSeconds: 0.3,
    targetSelector: 'triggeringEntity',
    effects: [{ fx: 'damage', amount: 90 }], // TEST — reflected for reduced damage
    ui: { label: 'Mirror Shield', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 AD-15 Anchor Field (displacement resistance approximated as a periodic
    // damage-taken aura; Conduit-defend condition dropped — deviation)
    id: 'ab-ad-anchor-field',
    name: 'Anchor Field',
    trigger: { when: 'timer', period: 16 }, // TEST
    internalCooldown: 16,
    telegraphSeconds: 0.4,
    targetSelector: 'self',
    effects: [{ fx: 'auraBuff', radius: 8, stat: 'damageTaken', mult: 0.9, duration: 8 }], // TEST
    ui: { label: 'Anchor Field', showFloatingLabel: false },
  },
  {
    // bible v0.2.4 AD-16 Time Split (delayed echo line approximated as an immediate strike
    // plus 50%-strength splash along the impact — deviation)
    id: 'ab-ad-time-split',
    name: 'Time Split',
    trigger: { when: 'largeEnemyInRange', range: 9 },
    internalCooldown: 20, // TEST
    telegraphSeconds: 0.5,
    targetSelector: 'triggeringEntity',
    effects: [
      { fx: 'damage', amount: 120 }, // TEST
      { fx: 'areaDamage', amount: 60, radius: 3 }, // echo: 50% of the strike (bible ratio)
    ],
    ui: { label: 'Time Split', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 AD-16 Rewind Position (position+health rewind approximated as a backward
    // blink plus a burst heal — deviation; the 6 s no-heal clause is literal)
    id: 'ab-ad-rewind-position',
    name: 'Rewind Position',
    trigger: { when: 'selfBelowHealth', fraction: 0.35, oncePerLife: true }, // bible: below 35%
    internalCooldown: 9999,
    telegraphSeconds: 0.3,
    targetSelector: 'self',
    effects: [
      { fx: 'teleport', distance: 8, direction: 'backward' }, // TEST
      { fx: 'heal', amount: 280 }, // TEST
      { fx: 'healReduction', mult: 0, duration: 6 }, // bible: cannot be healed for 6 s
    ],
    ui: { label: 'Rewind Position', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 AD-17 Drone Deck — 75/50/25% thresholds collapsed into two one-shot
    // launches (deviation); the repair drone becomes a heal-over-time
    id: 'ab-ad-drone-deck',
    name: 'Drone Deck',
    trigger: { when: 'selfBelowHealth', fraction: 0.66, oncePerLife: true }, // TEST
    internalCooldown: 9999,
    telegraphSeconds: 0.5,
    targetSelector: 'self',
    effects: [
      { fx: 'spawnTemporary', unitId: 'u-ad-shield-drone', count: 2, duration: 25 }, // TEST duration
      { fx: 'heal', amount: 220, overSeconds: 10 }, // TEST — repair drone
    ],
    ui: { label: 'Drone Deck', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 AD-17 Drone Deck (second threshold)
    id: 'ab-ad-drone-deck-reserve',
    name: 'Drone Deck',
    trigger: { when: 'selfBelowHealth', fraction: 0.33, oncePerLife: true }, // TEST
    internalCooldown: 9999,
    telegraphSeconds: 0.5,
    targetSelector: 'self',
    effects: [
      { fx: 'spawnTemporary', unitId: 'u-ad-shield-drone', count: 2, duration: 25 }, // TEST duration
      { fx: 'heal', amount: 220, overSeconds: 10 }, // TEST — repair drone
    ],
    ui: { label: 'Drone Deck', showFloatingLabel: true },
  },
  {
    // owner request (not in bible): vehicle giants surge to close distance — charge extension
    id: 'ab-ad-ark-charge',
    name: 'Solar Surge',
    trigger: { when: 'enemyInRange', range: 18, minRange: 6 },
    internalCooldown: 30, // TEST
    telegraphSeconds: 0.6,
    targetSelector: 'triggeringEntity',
    effects: [
      { fx: 'charge', speedMult: 2.8, durationSeconds: 2.2, impactDamage: 100, impactSlowMult: 0.6, impactSlowSeconds: 2 }, // TEST
    ],
    ui: { label: 'Charge', showFloatingLabel: true },
  },
];

// ---------------------------------------------------------------- buildings

export const AD_BUILDINGS: BuildingDef[] = [
  {
    id: 'b-ad-meridian-prism',
    name: 'Meridian Prism',
    faction: 'astral-dominion',
    kind: 'core',
    maxHealth: 3350, // bible 6,300 × owner's ~0.53 TTK factor
    armor: 'light',
    footprint: { w: 8, h: 8 },
    isMajorStructure: true,
    buildTime: 0,
    placement: 'core-territory',
    conduit: true, // the Core anchors the network
    attack: {
      damage: 125, // bible AD-01 Prism Lance
      period: 3.0,
      range: 22,
      projectileSpeed: 30,
      damageVs: { large: 1.2, giant: 1.3 },
      targetPriority: ['giant', 'large', 'siege', 'squad'],
    },
    // Core Annex System (owner spec 2026-07-13): three permanent sockets, all
    // eventually buildable — the strategic choice is build ORDER. Replaces the
    // v0.2 Phase Barrier / Conduit Nexus / Celestial Dock track (README Deviations).
    upgrades: [
      {
        id: 'up-ad-phase-barrier',
        name: 'Phase Barrier',
        icon: '🔮',
        socket: 'top',
        cost: { power: 220, essence: 55 }, // spec
        buildTime: 15, // owner request 2026-07-13
        rulesText:
          'Repulsion Cycle recharges 4 s faster and displaced Small/Medium attackers are slowed. The Core gains a regenerating phase shield, +3 shelter, and once per 60 s a dying worker near the Core is translated into shelter at 1 health.',
        effects: [
          { kind: 'repulsionHaste', seconds: 4 }, // spec
          { kind: 'displacedSlow', magnitude: 0.5, duration: 4 }, // spec // TEST magnitude
          { kind: 'coreShield', amount: 400, regenPerSecond: 8 }, // spec // TEST numbers
          { kind: 'shelterBonus', amount: 3 }, // spec
          { kind: 'workerDeathSave', cooldown: 60 }, // spec
          // spec's "workers phase the final distance into shelter" folded into the
          // death-save translation (no partial-path teleport) — see README Deviations
        ],
      },
      {
        id: 'up-ad-conduit-nexus',
        name: 'Conduit Nexus',
        icon: '🕸',
        socket: 'left',
        cost: { power: 250, essence: 65 }, // spec
        buildTime: 15, // owner request 2026-07-13
        rulesText:
          '+3 Workforce; carrying workers move 15% faster inside the Conduit network. Every 60 s, transfer up to four sheltered workers to a connected Conduit. Creates a Prism Skiff every 120 s — E: Establish Astral Relay.',
        effects: [
          { kind: 'workforceBonus', amount: 3 }, // spec
          { kind: 'conduitCarryHaste', mult: 1.15 }, // spec
        ],
        workerTransfer: { cooldown: 60, maxWorkers: 4, arriveSeconds: 3 }, // spec
        spawner: { unitId: 'u-ad-prism-skiff', period: 120, maxAlive: 1 }, // spec
      },
      {
        id: 'up-ad-echo-observatory',
        name: 'Echo Observatory',
        icon: '🔭',
        socket: 'bottom',
        cost: { power: 280, essence: 85 }, // spec
        buildTime: 15, // owner request 2026-07-13
        rulesText:
          'Stores one Echo Charge every 100 s. While armed, the next squad finished at a connected location is duplicated: 60% strength, 40 s lifetime, no Command, half damage to Cores; fizzles if its arrival Conduit dies within 8 s.',
        echo: {
          period: 100, // spec
          healthMult: 0.6, // spec
          damageMult: 0.6, // spec
          duration: 40, // spec
          structureDamageMult: 0.5, // spec
          linkSeconds: 8, // spec
        },
      },
    ],
    ui: { color: 0x9fb8d8, label: 'Meridian Prism' },
  },
  {
    // Core Annex System: free forward network node phased in from a Prism Skiff.
    // Especially vulnerable while phasing (selfBuilding fragility handled in-sim).
    id: 'b-ad-astral-relay',
    name: 'Astral Relay',
    faction: 'astral-dominion',
    kind: 'defense',
    maxHealth: 700, // spec: low health // TEST
    armor: 'none',
    footprint: { w: 3, h: 3 },
    isMajorStructure: false,
    buildTime: 40, // spec: phases in over 40 s
    placement: 'both',
    selfBuilding: true, // spec: free deployed buildings need no workers
    conduit: true, // spec: extends the Conduit network
    summonAnchor: { maxAscension: 1, radius: 14 }, // spec: basic Astral summoning location
    ui: { color: 0xa8c8ec, label: 'Astral Relay' },
  },
  {
    id: 'b-ad-site-tower',
    name: 'Watch Prism',
    faction: 'astral-dominion',
    kind: 'defense',
    maxHealth: 1400,
    armor: 'light',
    footprint: { w: 3, h: 3 },
    isMajorStructure: false,
    buildTime: 0,
    placement: 'site-radius',
    producesWorkers: true,
    isSiteTower: true,
    conduit: true, // site prisms extend the network
    signatureText: 'Refracted Defense chains to a second target while this site is contested.',
    traits: [{ kind: 'contestedChain', damageMult: 0.55 }],
    attack: {
      damage: 36,
      period: 1.2,
      range: 15,
      projectileSpeed: 30,
      targetPriority: ['squad', 'worker', 'large', 'giant'],
    },
    attackRequiresUpgrade: true,
    upgrade: {
      id: 'up-ad-focus-array',
      name: 'Focus Array',
      cost: { power: 150, essence: 0 },
      rulesText: 'Focuses the prism into a beam that automatically strikes nearby enemies.',
    },
    ui: { color: 0xa8c0e0, label: 'Watch Prism' },
  },
  {
    id: 'b-ad-lensing-spire',
    name: 'Lensing Spire',
    faction: 'astral-dominion',
    kind: 'defense',
    maxHealth: 925, // TEST
    armor: 'light',
    footprint: { w: 3, h: 3 },
    isMajorStructure: true,
    buildTime: 29, // TEST
    placement: 'both',
    conduit: true,
    signatureText: 'A continuous beam ramps while focused on one target, then overheats; Split Prism toggles swarm coverage.',
    traits: [{ kind: 'focusBeam', maxMult: 2.1, rampPerShot: 0.18, overheatShots: 8, overheatSeconds: 5 }],
    attack: {
      damage: 34, // TEST
      period: 0.65,
      range: 17,
      projectileSpeed: 38,
      targetPriority: ['large', 'giant', 'squad', 'worker'],
    },
    upgrade: {
      id: 'up-ad-split-prism',
      name: 'Split Prism',
      cost: { power: 185, essence: 60 }, // TEST
      rulesText: 'Toggle between one powerful beam and two weaker beams for swarm defense.',
    },
    ui: { color: 0x78bde5, label: 'Lensing Spire' },
  },
  {
    id: 'b-ad-castle-wall',
    name: 'Hardlight Wall',
    faction: 'astral-dominion',
    kind: 'wall',
    maxHealth: 1300,
    armor: 'heavy',
    footprint: { w: 2, h: 12 },
    isMajorStructure: false,
    buildTime: 24, // TEST — owner: walls take a while to raise (AD keeps its faster-phase edge)
    placement: 'both',
    selfBuilding: true,
    mountSlots: 2,
    signatureText: 'Phase Recast can dissolve and reconstruct this wall at another nearby position after a vulnerable channel.',
    ui: { color: 0xb8d0f0, label: 'Hardlight Wall' },
  },
  {
    id: 'b-ad-ritual-standard',
    name: 'Convergence Pylon',
    faction: 'astral-dominion',
    kind: 'defense',
    maxHealth: 500,
    armor: 'none',
    footprint: { w: 2, h: 2 },
    isMajorStructure: false,
    buildTime: 0,
    placement: 'both',
    signatureText: 'Network Convergence accelerates its ritual for each connected Conduit, but broken links cause backlash.',
    ui: { color: 0x6fd8e8, label: 'Convergence Pylon' },
  },
  {
    // Prismatic Decoy: short-lived false Core image (auto-acquisition bait)
    id: 'b-ad-decoy',
    name: 'Prismatic Decoy',
    faction: 'astral-dominion',
    kind: 'defense',
    maxHealth: 600,
    armor: 'none',
    footprint: { w: 3, h: 3 },
    isMajorStructure: false,
    buildTime: 0,
    placement: 'both',
    signatureText: 'Shatter Image reduces enemy accuracy and displaces Small units when the Decoy is destroyed or dismissed.',
    ui: { color: 0xc8e0f8, label: 'Decoy' },
  },
  {
    id: 'b-ad-precision-foundry',
    name: 'Precision Foundry',
    faction: 'astral-dominion',
    kind: 'production',
    maxHealth: 1100, // bible AD-05
    armor: 'light',
    footprint: { w: 5, h: 5 },
    isMajorStructure: true,
    buildTime: 38,
    placement: 'both',
    signatureText: 'Targeting Solution marks the highest-value visible enemy every 35 s for coordinated precision fire.',
    traits: [{ kind: 'periodicMark', radius: 24, cooldown: 35, duration: 8, markId: 'building-mark' }],
    roster: {
      queueSize: 5,
      entries: [
        { unitId: 'u-ad-arc-snipers', cost: { power: 160, essence: 30, command: 4 }, trainTime: 34, ascension: 2 },
        // v0.2.4 precision roster additions (train costs TEST)
      ],
    },
    upgrade: {
      id: 'up-ad-perfect-calibration',
      name: 'Perfect Calibration',
      cost: { power: 210, essence: 65 },
      rulesText: 'The first precision hit against a newly marked target interrupts its attack or channel.',
    },
    ui: { color: 0x8fa8c8, label: 'Precision Foundry' },
  },
  {
    id: 'b-ad-conduit-forge',
    name: 'Conduit Forge',
    faction: 'astral-dominion',
    kind: 'production',
    maxHealth: 950, // bible AD-06
    armor: 'light',
    footprint: { w: 4, h: 4 },
    isMajorStructure: true,
    buildTime: 30,
    placement: 'both',
    conduit: true,
    signatureText: 'Remote Muster can deploy completed squads from any connected Conduit, with a training delay until upgraded.',
    traits: [{ kind: 'remoteDeployment', extraTrainMult: 1.2, arrivalShield: 180, requiresMode: true }],
    roster: {
      queueSize: 5,
      entries: [
        { unitId: 'u-ad-blink-infantry', cost: { power: 95, essence: 0, command: 3 }, trainTime: 24, ascension: 1 },
        { unitId: 'u-ad-shield-drone', cost: { power: 100, essence: 0, command: 3 }, trainTime: 25, ascension: 1 },
        // v0.2.4 network-hub roster additions — the bible assigns the broad roster to the
        // Phase Citadel (AD-19); the Conduit Forge is the closest-function existing
        // building, so it hosts them (deviation). Train costs TEST.
      ],
    },
    upgrade: {
      id: 'up-ad-stable-transit',
      name: 'Stable Transit',
      cost: { power: 160, essence: 30 },
      rulesText: 'Removes most remote-production delay and grants a temporary arrival shield.',
    },
    ui: { color: 0x7a98c8, label: 'Conduit Forge' },
  },
  {
    id: 'b-ad-starforge-dock',
    name: 'Starforge Dock',
    faction: 'astral-dominion',
    kind: 'production',
    maxHealth: 1350, // bible AD-07
    armor: 'moderate',
    footprint: { w: 5, h: 5 },
    isMajorStructure: true,
    buildTime: 49,
    placement: 'both',
    conduit: true,
    signatureText: 'Phase Launch materializes produced Siege Orbs at a connected Site Tower or Conduit.',
    traits: [{ kind: 'remoteDeployment', extraTrainMult: 1, arrivalShield: 0 }],
    roster: {
      queueSize: 4,
      entries: [
        { unitId: 'u-ad-siege-orb', cost: { power: 270, essence: 90, command: 8 }, trainTime: 49, ascension: 3 },
        // v0.2.4 construct-and-air roster additions (train costs TEST)
      ],
    },
    upgrade: {
      id: 'up-ad-starforge-lattice',
      name: 'Starforge Lattice',
      cost: { power: 240, essence: 90 },
      rulesText: 'The first attack of each produced Siege Orb creates a brief gravity pull around its target.',
    },
    ui: { color: 0x6a88b8, label: 'Starforge Dock' },
  },
  {
    id: 'b-ad-aether-refinery',
    name: 'Aether Refinery',
    faction: 'astral-dominion',
    kind: 'production',
    maxHealth: 850, // bible AD-08
    armor: 'light',
    footprint: { w: 4, h: 4 },
    isMajorStructure: true,
    buildTime: 35,
    placement: 'both',
    conduit: true,
    dropOff: ['power', 'essence'],
    workforceBonus: 3,
    signatureText: 'Spectrum Alignment toggles Power or Essence focus; Prismatic Overflow shields nearby connected structures.',
    roster: {
      queueSize: 3,
      entries: [
        { unitId: 'u-ad-phase-engineer', cost: { power: 105, essence: 10 }, trainTime: 29, ascension: 2 },
      ],
    },
    upgrade: {
      id: 'up-ad-pure-spectrum',
      name: 'Pure Spectrum',
      cost: { power: 175, essence: 45 },
      rulesText: 'Prismatic Overflow travels through one additional connected Conduit structure.',
    },
    ui: { color: 0x88b0d8, label: 'Aether Refinery' },
  },
];

// ---------------------------------------------------------------- spells / traps / rituals

export const AD_SPELLS: SpellDef[] = [
  {
    // bible AD-17
    id: 'sp-ad-phase-lock',
    name: 'Phase Lock',
    faction: 'astral-dominion',
    targeting: { shape: 'entity', validTargets: 'enemy-squad', range: 'global' },
    effects: [],
    controlLock: { rootSeconds: 4, largeSlow: 0.45, giantSlow: 0.2, slowSeconds: 4 },
  },
  {
    // bible AD-18: five crystalline impacts (highest-Command finisher folded into the
    // shared bombardment model — deviation)
    id: 'sp-ad-astral-barrage',
    name: 'Astral Barrage',
    faction: 'astral-dominion',
    targeting: { shape: 'circle', radius: 8, range: 'global' },
    zoneDuration: 8,
    effects: [],
    bombardment: {
      telegraphSeconds: 2,
      impacts: 5,
      impactInterval: 0.8,
      impactRadius: 4,
      buildingDamage: 180,
      squadDamage: 130,
      workerDamage: 80,
      coreDamageMult: 0.3,
      largeMult: 1.3,
    },
  },
  {
    // bible AD-19
    id: 'sp-ad-emergency-translation',
    name: 'Emergency Translation',
    faction: 'astral-dominion',
    targeting: { shape: 'circle', radius: 6, range: 'global' },
    effects: [],
    massTeleport: { maxSquads: 6, shield: 250, shieldSeconds: 4, telegraphSeconds: 5 },
  },
];

export const AD_TRAPS: TrapDef[] = [
  {
    // bible AD-20
    id: 'tr-ad-gravity-well',
    name: 'Gravity Well',
    faction: 'astral-dominion',
    behavior: 'gravityWell',
    pieceHealth: 280,
    perDeploymentPieces: 1,
    buildTime: 8, // TEST
    triggerRadius: 6,
    triggerSizeTags: ['small', 'medium', 'large', 'giant'],
    effects: [{ fx: 'damage', amount: 30 }],
    maxActiveFields: 2,
    minSquads: 2,
  },
  {
    // bible AD-21
    id: 'tr-ad-seal-of-denial',
    name: 'Seal of Denial',
    faction: 'astral-dominion',
    behavior: 'spellSeal',
    pieceHealth: 420,
    perDeploymentPieces: 1,
    buildTime: 8, // TEST
    triggerRadius: 12,
    triggerSizeTags: [],
    effects: [],
    maxActiveFields: 1,
  },
];

export const AD_RITUALS: RitualDef[] = [
  {
    // bible AD-22
    id: 'rit-ad-convergence-protocol',
    name: 'Convergence Protocol',
    faction: 'astral-dominion',
    channelSeconds: 12,
    channelAt: 'both',
    standardHealth: 520, // TEST
    refundFraction: 0.35,
  },
];

export const AD_BOSS_EXTRAS: Record<string, BossExtras> = {
  'boss-ad-xyra': { summonSeconds: 15, announcement: 'local' },
  'boss-ad-arbiter': { summonSeconds: 30, announcement: 'global' }, // owner request 2026-07-13
  'boss-ad-war-engine': {
    summonSeconds: 60, // owner request 2026-07-13
    announcement: 'global',
    // escort-class unlocks (bible AD-25)
    escortClassBonuses: {
      infantryDamageTakenMult: 0.85,
      rangedAttackSpeedMult: 1.1,
      siegeStructureDamageMult: 1.15,
      supportRegenPerSecond: 10,
    },
  },
};

// ---------------------------------------------------------------- cards (deck pool)

export const AD_CARDS: CardDef[] = [
  {
    id: 'card-ad-precision-foundry',
    name: 'Precision Foundry',
    faction: 'astral-dominion',
    category: 'building',
    subjectId: 'b-ad-precision-foundry',
    cost: { power: 245, essence: 30 },
    ascension: 2,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'limited', maxUses: 2, cooldown: 65 },
    hotkey: '',
    ui: {
      icon: '🎯',
      roleLine: 'Precision & heavy roster',
      rulesText: 'Builds only Arc Snipers. Targeting Solution marks high-value enemies; related constructs and tanks are direct cards.',
    },
  },
  {
    id: 'card-ad-conduit-forge',
    name: 'Conduit Forge',
    faction: 'astral-dominion',
    category: 'building',
    subjectId: 'b-ad-conduit-forge',
    cost: { power: 180, essence: 0 },
    ascension: 1,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 55 },
    maxActive: 3,
    hotkey: '',
    ui: {
      icon: '⚡',
      roleLine: 'Basic army & network node',
      rulesText:
        'A two-unit barracks and Conduit node. Trains Blink Infantry and Shield Drones; Remote Muster deploys them from linked Conduits.',
    },
  },
  {
    id: 'card-ad-starforge-dock',
    name: 'Starforge Dock',
    faction: 'astral-dominion',
    category: 'building',
    subjectId: 'b-ad-starforge-dock',
    cost: { power: 310, essence: 90 },
    ascension: 3,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'limited', maxUses: 2, cooldown: 80 },
    maxActive: 1,
    hotkey: '',
    ui: {
      icon: '🛰',
      roleLine: 'Heavy production & boss support',
      rulesText: 'Assembles only Siege Orbs and can phase-launch them from the connected network. Star Lancers remain a direct card.',
    },
  },
  {
    id: 'card-ad-aether-refinery',
    name: 'Aether Refinery',
    faction: 'astral-dominion',
    category: 'building',
    subjectId: 'b-ad-aether-refinery',
    cost: { power: 200, essence: 0 },
    ascension: 2,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 60 },
    maxActive: 2,
    hotkey: '',
    ui: {
      icon: '💠',
      roleLine: 'Essence & logistics economy',
      rulesText: '+3 Workforce, both-resource drop-off, Conduit link, and Phase Engineer production. Spectrum Alignment supports the selected economy.',
    },
  },
  {
    id: 'card-ad-blink-infantry',
    name: 'Blink Infantry',
    faction: 'astral-dominion',
    category: 'unit',
    subjectId: 'u-ad-blink-infantry',
    cost: { power: 115, essence: 0, command: 3 },
    ascension: 1,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 34 },
    hotkey: '',
    ui: {
      icon: '⚔',
      roleLine: 'Flexible teleporting frontline',
      rulesText: 'Five energy-blade soldiers. Tactical Blink folds them out of bad melee.',
    },
  },
  {
    // bible v0.2.4 AD-04
    id: 'card-ad-rift-hounds',
    name: 'Rift Hounds',
    faction: 'astral-dominion',
    category: 'unit',
    subjectId: 'u-ad-rift-hounds',
    cost: { power: 95, essence: 0, command: 2 },
    ascension: 1,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 30 },
    hotkey: '',
    ui: {
      icon: '🐾',
      roleLine: 'Fast pursuit pack',
      rulesText: 'Five crystalline hounds. Phase Chase folds them onto fleeing targets and slows the catch.',
    },
  },
  {
    // bible v0.2.4 AD-08
    id: 'card-ad-prism-adepts',
    name: 'Prism Adepts',
    faction: 'astral-dominion',
    category: 'unit',
    subjectId: 'u-ad-prism-adepts',
    cost: { power: 155, essence: 35, command: 4 },
    ascension: 2,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 60 },
    hotkey: '',
    ui: {
      icon: '🔷',
      roleLine: 'Beam refraction & shielding',
      rulesText: 'Three crystal mages fire long refracted beams; Prism Ward hardens nearby squads against bursts.',
    },
  },
  {
    // bible v0.2.4 AD-09
    id: 'card-ad-null-monks',
    name: 'Null Monks',
    faction: 'astral-dominion',
    category: 'unit',
    subjectId: 'u-ad-null-monks',
    cost: { power: 160, essence: 40, command: 4 },
    ascension: 2,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 64 },
    hotkey: '',
    ui: {
      icon: '⭕',
      roleLine: 'Spell denial & buff theft',
      rulesText: 'Four void-ring monks. Null Field saps enemy recovery and output; Borrowed Formula echoes it to allies.',
    },
  },
  {
    // bible v0.2.4 AD-10
    id: 'card-ad-comet-riders',
    name: 'Comet Riders',
    faction: 'astral-dominion',
    category: 'unit',
    subjectId: 'u-ad-comet-riders',
    cost: { power: 190, essence: 40, command: 5 },
    ascension: 2,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 72 },
    hotkey: '',
    ui: {
      icon: '💫',
      roleLine: 'Dive bomber & lane pressure',
      rulesText: 'Three comet-surfing riders. Comet Dive slams fortifications and dulls their return fire.',
    },
  },
  {
    // bible v0.2.4 AD-13
    id: 'card-ad-vector-blades',
    name: 'Vector Blades',
    faction: 'astral-dominion',
    category: 'unit',
    subjectId: 'u-ad-vector-blades',
    cost: { power: 205, essence: 60, command: 5 },
    ascension: 3,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 70 },
    hotkey: '',
    ui: {
      icon: '🔀',
      roleLine: 'Backline teleport assassins',
      rulesText: 'Four dual-bladed killers. Weak-Side Translation folds them onto exposed squads and severs their support.',
    },
  },
  {
    // bible v0.2.4 AD-14
    id: 'card-ad-gravity-weavers',
    name: 'Gravity Weavers',
    faction: 'astral-dominion',
    category: 'unit',
    subjectId: 'u-ad-gravity-weavers',
    cost: { power: 190, essence: 70, command: 4 },
    ascension: 3,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 74 },
    hotkey: '',
    ui: {
      icon: '🌌',
      roleLine: 'Displacement & formation control',
      rulesText: 'Three mass-benders. Suspension Knot pins infantry clusters; Mass Inversion throws back charging giants.',
    },
  },
  {
    // bible v0.2.4 AD-15
    id: 'card-ad-celestial-wardens',
    name: 'Celestial Wardens',
    faction: 'astral-dominion',
    category: 'unit',
    subjectId: 'u-ad-celestial-wardens',
    cost: { power: 230, essence: 75, command: 6 },
    ascension: 3,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 80 },
    hotkey: '',
    ui: {
      icon: '🛡',
      roleLine: 'Anchor & projectile reflection',
      rulesText: 'A construct pair. Mirror Shield returns heavy shots to their source; Anchor Field steadies the line.',
    },
  },
  {
    // bible v0.2.4 AD-16
    id: 'card-ad-chrono-lancers',
    name: 'Chrono Lancers',
    faction: 'astral-dominion',
    category: 'unit',
    subjectId: 'u-ad-chrono-lancers',
    cost: { power: 310, essence: 120, command: 8 },
    ascension: 4,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'limited', maxUses: 2, cooldown: 105 },
    hotkey: '',
    ui: {
      icon: '⏳',
      roleLine: 'Time-split assault cavalry',
      rulesText: 'Five lancers whose charges echo a moment later. Below 35% health they rewind once — then cannot be healed.',
    },
  },
  {
    // bible v0.2.4 AD-17
    id: 'card-ad-solar-ark',
    name: 'Solar Ark',
    faction: 'astral-dominion',
    category: 'unit',
    subjectId: 'u-ad-solar-ark',
    cost: { power: 360, essence: 150, command: 10 },
    ascension: 4,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'limited', maxUses: 2, cooldown: 115 },
    hotkey: '',
    ui: {
      icon: '☀',
      roleLine: 'Carrier & beam platform',
      rulesText: 'A drifting solar astrolabe. Sunline beams crack armor; damaged bays launch shield and repair drones.',
    },
  },
  {
    id: 'card-ad-phase-lock',
    name: 'Phase Lock',
    faction: 'astral-dominion',
    category: 'spell',
    subjectId: 'sp-ad-phase-lock',
    cost: { power: 90, essence: 0 },
    ascension: 1,
    summonLocations: ['anywhere-friendly'],
    useModel: { kind: 'reusable', cooldown: 54 },
    hotkey: '',
    ui: {
      icon: '🔒',
      roleLine: 'Reusable control',
      rulesText:
        'Lock an enemy squad in a rigid field: infantry immobilized 4 s (but 25% harder to kill); Large slowed 45%; Giants 20%.',
    },
  },
  {
    id: 'card-ad-astral-barrage',
    name: 'Astral Barrage',
    faction: 'astral-dominion',
    category: 'spell',
    subjectId: 'sp-ad-astral-barrage',
    cost: { power: 145, essence: 30 },
    ascension: 2,
    summonLocations: ['anywhere-friendly'],
    useModel: { kind: 'limited', maxUses: 3, cooldown: 72 },
    hotkey: '',
    ui: {
      icon: '☄',
      roleLine: 'Area precision damage',
      rulesText: 'Five crystalline projectiles descend in sequence over the area. Buildings resist; traps do not.',
    },
  },
  {
    id: 'card-ad-translation',
    name: 'Emergency Translation',
    faction: 'astral-dominion',
    category: 'spell',
    subjectId: 'sp-ad-emergency-translation',
    cost: { power: 240, essence: 175 },
    ascension: 4,
    summonLocations: ['anywhere-friendly'],
    useModel: { kind: 'once' },
    hotkey: '',
    ui: {
      icon: '🌀',
      roleLine: 'Ultimate army reposition',
      rulesText:
        'Once per match. Translates your 6 strongest non-Giant squads to the Core, a controlled site, or a linked conduit — each shielded on arrival.',
    },
  },
  {
    id: 'card-ad-gravity-well',
    name: 'Gravity Well',
    faction: 'astral-dominion',
    category: 'trap',
    subjectId: 'tr-ad-gravity-well',
    cost: { power: 120, essence: 15 },
    ascension: 2,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 62 },
    maxActive: 2,
    hotkey: '',
    ui: {
      icon: '🕳',
      roleLine: 'Anti-mobility trap',
      rulesText: 'Yanks infantry into its center and slows Large and Giant units 35% for 5 s.',
    },
  },
  {
    id: 'card-ad-seal-of-denial',
    name: 'Seal of Denial',
    faction: 'astral-dominion',
    category: 'trap',
    subjectId: 'tr-ad-seal-of-denial',
    cost: { power: 180, essence: 55 },
    ascension: 3,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'limited', maxUses: 2, cooldown: 60 },
    maxActive: 1,
    hotkey: '',
    ui: {
      icon: '🚫',
      roleLine: 'Spell-counter trap',
      rulesText:
        'Cancels the first enemy spell cast inside its radius (they recover 40% of the Power). Ultimates shatter it but resolve.',
    },
  },
  {
    id: 'card-ad-convergence',
    name: 'Convergence Protocol',
    faction: 'astral-dominion',
    category: 'ritual',
    subjectId: 'rit-ad-convergence-protocol',
    cost: { power: 250, essence: 120 },
    ascension: 3,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'once' },
    hotkey: '',
    ui: {
      icon: '🕸',
      roleLine: 'Perfect-network ritual',
      rulesText:
        'Channel 12 s. For 30 s: production runs 20% faster, Asc I–II cards may summon at linked conduits, and the army marches 15% faster.',
    },
  },
  {
    id: 'card-ad-xyra',
    name: 'Phase Hunter Xyra',
    faction: 'astral-dominion',
    category: 'boss',
    subjectId: 'boss-ad-xyra',
    cost: { power: 255, essence: 50, command: 7 },
    ascension: 2,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'once' },
    hotkey: '',
    ui: {
      icon: '🗡',
      roleLine: 'Raid boss',
      rulesText: 'Once per match. Folds space to reach isolated workers and support units. Built to hunt, not to hold.',
    },
  },
  {
    id: 'card-ad-arbiter',
    name: 'Celestial Arbiter',
    faction: 'astral-dominion',
    category: 'boss',
    subjectId: 'boss-ad-arbiter',
    cost: { power: 400, essence: 135, command: 12 },
    ascension: 3,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'once' },
    hotkey: '',
    ui: {
      icon: '⚖',
      roleLine: 'Control boss',
      rulesText:
        'Once per match. Adjudicate Position hurls the most dangerous enemy squad out of the fight every 17 s.',
    },
  },
  {
    id: 'card-ad-war-engine',
    name: 'Celestial War Engine',
    faction: 'astral-dominion',
    category: 'boss',
    subjectId: 'boss-ad-war-engine',
    cost: { power: 630, essence: 275, command: 20 },
    ascension: 4,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'once' },
    hotkey: '',
    ui: {
      icon: '🌟',
      roleLine: 'Ascension IV ranged finisher',
      rulesText:
        'Once per match. 60 s announced descent. Solar Measure beams crack fortifications; escort classes unlock barriers, side cannons, and repairs.',
    },
  },
  {
    id: 'card-ad-targeting-construct',
    name: 'Targeting Construct',
    faction: 'astral-dominion',
    category: 'unit',
    subjectId: 'u-ad-targeting-construct',
    cost: { power: 120, essence: 20, command: 3 }, // TEST
    ascension: 2,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 46 }, // TEST
    hotkey: '',
    ui: { icon: '🎯', roleLine: 'Target support', rulesText: 'A fragile construct that coordinates marked-target fire.' },
  },
  {
    id: 'card-ad-phase-tank',
    name: 'Phase Assault Tank',
    faction: 'astral-dominion',
    category: 'unit',
    subjectId: 'u-ad-phase-tank',
    cost: { power: 255, essence: 75, command: 8 }, // TEST
    ascension: 3,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'limited', maxUses: 2, cooldown: 88 }, // TEST
    hotkey: '',
    ui: { icon: '🛸', roleLine: 'Breakthrough armor', rulesText: 'Heavy phase armor for cracking fortified positions.' },
  },
  {
    id: 'card-ad-star-lancer',
    name: 'Star Lancer',
    faction: 'astral-dominion',
    category: 'unit',
    subjectId: 'u-ad-star-lancer',
    cost: { power: 235, essence: 70, command: 7 }, // TEST
    ascension: 3,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'limited', maxUses: 2, cooldown: 84 }, // TEST
    hotkey: '',
    ui: { icon: '☄', roleLine: 'Anti-heavy cavalry', rulesText: 'Fast lancers specialized against Large and Giant enemies.' },
  },
  {
    id: 'card-ad-lensing-spire',
    name: 'Lensing Spire',
    faction: 'astral-dominion',
    category: 'building',
    subjectId: 'b-ad-lensing-spire',
    cost: { power: 210, essence: 50 }, // TEST
    ascension: 2,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 64 }, // TEST
    maxActive: 3,
    hotkey: '',
    ui: { icon: '🔭', roleLine: 'Dedicated focusing beam tower', rulesText: 'Its beam ramps against one target before overheating; guard its fragile Conduit link.' },
  },
];

/** Bible §8.4 "The Perfect Vector" test deck (slot order = hotkey order). */
export const AD_DEFAULT_DECK: string[] = [
  'card-ad-precision-foundry',
  'card-ad-conduit-forge',
  'card-ad-starforge-dock',
  'card-ad-aether-refinery',
  'card-ad-blink-infantry',
  'card-ad-phase-lock',
  'card-ad-astral-barrage',
  'card-ad-translation',
  'card-ad-gravity-well',
  'card-ad-seal-of-denial',
  'card-ad-convergence',
  'card-ad-xyra',
  'card-ad-arbiter',
  'card-ad-war-engine',
];
